import React, { useEffect, useState } from "react";
import { Pie, Bar, Line } from "react-chartjs-2";
import "../../../../helper/chartSetup";
import BusApiCall from "../../../../helper/axios";
import constants from "../../../../helper/constants";
import Loader from "../../../../component/loader";
import classes from "../dashboard/dashboard.module.css";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Input from "../../../../component/inputpm";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import UriEncode from "../../../../component/uriEncode/uriEncode";
import InfoWithMobilePopover from "../../../../component/tooltip";
import currencyFormatter from "../../../../helper/currencyFormatter";

const GraphPortlet = ({
  portlet,
  duration,
  customFromDate,
  customToDate,
  graphType,
  fetchPortletData,
  portletData,
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState(null); // For chart data
  const [loading, setLoading] = useState(true); // For loading state
  const [shipmentTypeList, setShipmentTypeList] = useState([]);
  const [shipmentType, setShipmentType] = useState("");

  const [statusList, setStatusList] = useState([]);
  const [statusType, setStatusType] = useState("");

  const [quoteTypeList, setQuoteTypeList] = useState([]);
  const [quoteType, setQuoteType] = useState("");

  const [quoteTypeMap, setquotesMap] = useState({});
  const [quoteTypeId, setQuoteTypeId] = useState("");

  const [orderTypeMap, setOrderTypeMap] = useState({});
  const [typeId, setTypeId] = useState("");

  const [tenantsList, setTenantsList] = useState([]);
  const [partnerId, setPartnerId] = useState("");

  const [isAllZero, setIsAllZero] = useState(false);

  const [selectedGraphType, setSelectedGraphType] = useState(
    portlet.graphType || "",
  );

  const handleGraphTypeChange = (event) => {
    const changeGraphType = event.target.value;
    setSelectedGraphType(changeGraphType);
    fetchDataForGraphType(changeGraphType);
  };

  const fetchDataForGraphType = async (chartType) => {
    if (!chartType) return;

    setLoading(true);
    try {
      let url = "";
      if (duration === "custom") {
        let fromDate = customFromDate
          ? encodeURIComponent(convertDateParam(customFromDate))
          : "";
        let toDate = customToDate
          ? encodeURIComponent(convertTillDateParam(customToDate))
          : "";
        url = `${constants.busInternalUrl}/graphs/${portlet.id}/records?fromDate=${fromDate}&toDate=${toDate}&typeId=&statusId=&quoteTypeId=&partnerId=&chartType=${chartType}`;
      } else {
        url = `${constants.busInternalUrl}/graphs/${portlet.id}/records?duration=${duration}&typeId=&statusId=&quoteTypeId=&partnerId=&chartType=${chartType}`;
      }

      const response = await BusApiCall("GET", url, {});
      const data = response.data;

      processPortletDataWithType(data, chartType);
    } catch (error) {
      console.error("Error fetching graph data:", error);
      setLoading(false);
    }
  };

  const handleShipmentTypeChange = (event) => {
    setShipmentType(event.target.value);
    setTypeId("");
  };
  const handleStatusChange = (event) => {
    setStatusType(event.target.value);
  };
  const handleQuoteTypeChange = (event) => {
    setQuoteType(event.target.value);
    setTypeId("");
  };
  const handleQuoteStatusChange = (event) => {
    setTypeId("");
    setTypeId(event.target.value);
    setQuoteType("");
    setShipmentType("");
  };
  const handlePartnerIdChange = (event) => {
    setPartnerId(event.target.value);
  };
  const handleOrderStatusChange = (event) => {
    setTypeId("");
    setTypeId(event.target.value);
    setQuoteType("");
    setShipmentType("");
  };

  // Chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutoutPercentage: 50,
    legend: {
      display: true,
      position: "right",
      align: "start",
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          return Math.round(value);
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (value <= 10) return Number.isInteger(value) ? value : null;
            if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
            if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
            return value;
          },
          autoSkip: true,
          maxTicksLimit: 8,
        },
        min: 0,
        max: isAllZero ? 10 : undefined,
        title: {
          display: true,
          text: portletData?.currencySymbol
            ? `${portlet.name} (${currencyFormatter("", false)})`
            : portlet.name,
          font: { size: 14, weight: "normal" },
          color: "rgba(0,0,0,0.87)",
        },
        grid: {
          drawBorder: false,
          color: "rgba(0,0,0,0.05)",
        },
      },
      x: {
        title: {
          display: true,
          text:
            duration === "week"
              ? "Day"
              : duration === "month"
                ? "Week"
                : duration === "quarterly" || duration === "year"
                  ? "Month"
                  : "Duration",
          font: { size: 14, weight: "normal" },
          color: "rgba(0,0,0,0.87)",
        },
        grid: {
          drawBorder: false,
          color: "rgba(0,0,0,0.05)",
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.formattedValue || Math.round(context.raw);
            return `${label}: ${value}`;
          },
        },
      },
      legend: {
        position: "top",
        labels: {
          boxWidth: 28,
          boxHeight: 14,
          borderRadius: 0,
          usePointStyle: false,
          padding: 10,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
        text: "Bar Chart",
      },
    },
  };


  const lineChartOptions = {
    responsive: true,
    elements: {
      line: {
        tension: 0.4, //set default tension for all lines to prevent unwanted animation when props load
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (value <= 10) {
              return Number.isInteger(value) ? value : null;
            }
            if (value >= 1_000_000) {
              return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
            } else if (value >= 1_000) {
              return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
            }
            return value;
          },
          autoSkip: true,
          maxTicksLimit: 8,
        },
        min: 0,
        max: isAllZero ? 10 : undefined,
        title: {
          display: true,
          text: portletData?.currencySymbol
            ? `${portlet.name} (${currencyFormatter("", false)})`
            : portlet.name,
          font: {
            size: 14,
            weight: "normal",
          },
          color: "rgba(0,0,0,0.87)",
        },
        grid: {
          drawBorder: false,
          color: "rgba(0,0,0,0.05)",
        },
      },
      x: {
        title: {
          display: true,
          text:
            duration === "week"
              ? "Day"
              : duration === "month"
                ? "Week"
                : duration === "quarterly"
                  ? "Month"
                  : duration === "year"
                    ? "Month"
                    : "Duration",
          font: {
            size: 14,
            weight: "normal",
          },
          color: "rgba(0,0,0,0.87)",
        },
        grid: {
          drawBorder: false,
          color: "rgba(0,0,0,0.05)",
        },
      },
    },
    plugins: {
      tooltip: {
        // callbacks: {
        //   label: function (context) {
        //     const value = context.raw;
        //     return Math.round(value);
        //   },
        // },
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.formattedValue || Math.round(context.raw);
            return `${label}: ${value}`;
          },
        }
      },
      legend: {
        position: "top",
        labels: {
          boxWidth: 25,
          boxHeight: 11,
          borderRadius: 0,
          usePointStyle: false,
          padding: 11,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
        text: "Line Chart",
      },
      fill: {
        target: false,
      },
    },
  };

  useEffect(() => {
    if (portletData) {
      processPortletData(portletData);
    }
  }, [portletData]);

  const convertDateParam = (date) => {
    return moment(date).format("yyyy-MM-DDTHH:mm:ss:SSS+00:00");
  };

  const convertTillDateParam = (date) => {
    return moment(date).format("yyyy-MM-DDT23:59:59:999+00:00");
  };

  const getRandomRGBA = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    // const a = Math.random().toFixed(2); // 0–1
    const a = 1;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const processPortletData = (data) => {
    var labels = [];
    var values = [];
    if (portlet.id == "SalesOrdersChart") {
      const parsedChartData = JSON.parse(data.salesOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "WorkOrdersChart") {
      const parsedChartData = JSON.parse(data.workOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PurchaseOrdersChart") {
      const parsedChartData = JSON.parse(data.purchaseOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "CustomerReturnChart") {
      const parsedChartData = JSON.parse(data.customerReturnData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "ShipmentPieChart") {
      const parsedChartData = JSON.parse(data.shipmentData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
      if (data.shipmentTypeList) {
        setShipmentTypeList(data.shipmentTypeList);
      }
    } else if (portlet.id == "SalesTopCustomerPie") {
      const parsedChartData = JSON.parse(data.salesCountData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "OrderCycleTimePie") {
      const parsedChartData = JSON.parse(data.salesCountData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PartnerOrdersPie") {
      const parsedChartData = JSON.parse(data.partnersTotalOrderJson);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PartnerOrderStatus") {
      const parsedChartData = JSON.parse(data.orderStatusJsonString);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (
      (portlet.id === "CustomerLineGraph" ||
        portlet.id === "LeadClosureGraph" ||
        portlet.id === "TotalAddOnLineGraph" ||
        portlet.id === "CustomerCountGraph" ||
        portlet.id === "TotalOrdersGraph" ||
        portlet.id === "TotalAddOnGraph" ||
        portlet.id === "ProductRevenueGraph" ||
        portlet.id === "OpCoStlmtlGraph" ||
        portlet.id === "PartnerFulfillGraph" ||
        portlet.id === "PartnerStlmtlGraph") &&
      selectedGraphType === "line"
    ) {
      labels = data.labels;
      values = data.chartData;
    } else if (
      (portlet.id === "LeadClosureBarGraph" ||
        portlet.id === "CustomerBarGraph" ||
        portlet.id === "TotalAddOnBarGraph" ||
        portlet.id === "LeadClosureGraph" ||
        portlet.id === "CustomerCountGraph" ||
        portlet.id === "TotalOrdersGraph" ||
        portlet.id === "TotalAddOnGraph" ||
        portlet.id === "ProductRevenueGraph" ||
        portlet.id === "OpCoStlmtlGraph" ||
        portlet.id === "PartnerFulfillGraph" ||
        portlet.id === "PartnerStlmtlGraph") &&
      selectedGraphType === "bar"
    ) {
      // const parsedChartData = JSON.parse(data.chartData);
      const parsedChartData = typeof data.chartData === 'string'
        ? JSON.parse(data.chartData)
        : data.chartData;
      const parsedLabels = typeof data.labels === 'string'
        ? JSON.parse(data.labels)
        : data.labels;
      labels = parsedLabels;
      values = parsedChartData;
    } else if (
      portlet.id == "SalesTopCustomerBar" ||
      portlet.id == "PriorityOrdersChart" ||
      portlet.id == "ShipmentBarChart" ||
      portlet.id == "QuotePerPersonChart" ||
      portlet.id == "OrderCycleTimeBar" ||
      portlet.id == "QuoteStatusBar" ||
      portlet.id == "OrderStatusBar" ||
      portlet.id == "SubscriptionStatus" ||
      portlet.id == "DunningCount" ||
      portlet.id == "InvoiceStatusCount"
    ) {
      // const parsedChartData = JSON.parse(data.chartData);
      const parsedChartData = typeof data.chartData === 'string'
        ? JSON.parse(data.chartData)
        : data.chartData;
      const parsedLabels = typeof data.labels === 'string'
        ? JSON.parse(data.labels)
        : data.labels;
      labels = parsedLabels.map((item) => item[1]);
      values = parsedChartData[0].data.map((item) => item[1]);

      if (data.shipmentTypeList) {
        setShipmentTypeList(data.shipmentTypeList);
      }
      if (data.statusList) {
        setStatusList(data.statusList);
      }
      if (data.quoteTypeList) {
        setQuoteTypeList(data.quoteTypeList);
      }
      if (data.quoteTypes) {
        setquotesMap(data.quoteTypes);
      }
      if (data.tenants) {
        setTenantsList(data.tenants);
      }
      if (data.orderTypes) {
        setOrderTypeMap(data.orderTypes);
      }
    }

    // Prepare chart data
    if (selectedGraphType === "line") {
      var refinedValues =
        values && Array.isArray(values)
          ? values.map((item, index) => {
            var obj = { ...item };
            obj.fill = false;
            if (index === 0) {
              obj.borderColor = "rgba(151, 71, 255, 1)";
              obj.backgroundColor = obj.borderColor;
            } else if (index === 1) {
              obj.borderColor = "rgba(255, 189, 189, 1)";
              obj.backgroundColor = obj.borderColor;
            } else {
              obj.borderColor = getRandomRGBA();
              obj.backgroundColor = obj.borderColor;
            }
            return obj;
          })
          : [];
      var isAllZero = refinedValues.every((dataset) =>
        dataset.data.every((value) => value === 0),
      );
      setIsAllZero(isAllZero);
      const preparedChartData = {
        labels: labels,
        datasets: [
          ...refinedValues,
          // {
          //   label: portlet.name,
          //   data: values,
          //   backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
          //   borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
          //   borderWidth: 1,
          // },
        ],
      };
      setChartData(preparedChartData);
    } else {
      if (selectedGraphType === "bar") {
        var refinedValues = values.map((item, index) => {
          var obj = { ...item };
          // obj.fill = false;
          if (index === 0) {
            obj.borderColor = "rgba(151, 71, 255, 1)";
            obj.backgroundColor = obj.borderColor;
          } else if (index === 1) {
            obj.borderColor = "rgba(255, 189, 189, 1)";
            obj.backgroundColor = obj.borderColor;
          } else {
            obj.borderColor = getRandomRGBA();
            obj.backgroundColor = obj.borderColor;
          }
          return obj;
        });
        const preparedChartData = {
          labels: labels,
          datasets: [
            ...refinedValues,
            // {
            //   label: portlet.name,
            //   data: values,
            //   backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
            //   borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
            //   borderWidth: 1,
            // },
          ],
        };
        setChartData(preparedChartData);
      } else {
        const preparedChartData = {
          labels: labels,
          datasets: [
            {
              label: portlet.name,
              data: values,
              // backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
              // borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
              backgroundColor: values.map((item) => "rgba(151, 71, 255, 1)"),
              borderColor: values.map((item) => "rgba(151, 71, 255, 1)"),
              borderWidth: 1,
            },
          ],
        };
        setChartData(preparedChartData);
      }
    }
    setLoading(false);
  };

  const processPortletDataWithType = (data, chartType) => {
    var labels = [];
    var values = [];

    if (portlet.id == "SalesOrdersChart") {
      const parsedChartData = JSON.parse(data.salesOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "WorkOrdersChart") {
      const parsedChartData = JSON.parse(data.workOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PurchaseOrdersChart") {
      const parsedChartData = JSON.parse(data.purchaseOrderData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "CustomerReturnChart") {
      const parsedChartData = JSON.parse(data.customerReturnData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "ShipmentPieChart") {
      const parsedChartData = JSON.parse(data.shipmentData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
      if (data.shipmentTypeList) {
        setShipmentTypeList(data.shipmentTypeList);
      }
    } else if (portlet.id == "SalesTopCustomerPie") {
      const parsedChartData = JSON.parse(data.salesCountData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "OrderCycleTimePie") {
      const parsedChartData = JSON.parse(data.salesCountData);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PartnerOrdersPie") {
      const parsedChartData = JSON.parse(data.partnersTotalOrderJson);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (portlet.id == "PartnerOrderStatus") {
      const parsedChartData = JSON.parse(data.orderStatusJsonString);
      labels = parsedChartData.map((item) => item.label);
      values = parsedChartData.map((item) => item.data);
    } else if (
      (portlet.id === "CustomerLineGraph" ||
        portlet.id === "LeadClosureGraph" ||
        portlet.id === "TotalAddOnLineGraph" ||
        portlet.id === "CustomerCountGraph" ||
        portlet.id === "TotalOrdersGraph" ||
        portlet.id === "TotalAddOnGraph" ||
        portlet.id === "ProductRevenueGraph" ||
        portlet.id === "OpCoStlmtlGraph" ||
        portlet.id === "PartnerFulfillGraph" ||
        portlet.id === "PartnerStlmtlGraph") &&
      chartType === "line"
    ) {
      labels = data.labels;
      values = data.chartData;
    } else if (
      (portlet.id === "LeadClosureBarGraph" ||
        portlet.id === "CustomerBarGraph" ||
        portlet.id === "TotalAddOnBarGraph" ||
        portlet.id === "LeadClosureGraph" ||
        portlet.id === "CustomerCountGraph" ||
        portlet.id === "TotalOrdersGraph" ||
        portlet.id === "TotalAddOnGraph" ||
        portlet.id === "ProductRevenueGraph" ||
        portlet.id === "OpCoStlmtlGraph" ||
        portlet.id === "PartnerFulfillGraph" ||
        portlet.id === "PartnerStlmtlGraph") &&
      chartType === "bar"
    ) {
      const parsedChartData = JSON.parse(data.chartData);
      const parsedLabels = JSON.parse(data.labels);
      labels = parsedLabels;
      values = parsedChartData;
      // labels = data.labels;
      // values = data.chartData;
    } else if (
      portlet.id == "SalesTopCustomerBar" ||
      portlet.id == "PriorityOrdersChart" ||
      portlet.id == "ShipmentBarChart" ||
      portlet.id == "QuotePerPersonChart" ||
      portlet.id == "OrderCycleTimeBar" ||
      portlet.id == "QuoteStatusBar" ||
      portlet.id == "OrderStatusBar" ||
      portlet.id == "SubscriptionStatus" ||
      portlet.id == "DunningCount" ||
      portlet.id == "InvoiceStatusCount"
    ) {
      const parsedChartData = safeParseJSON(data.chartData, []);
      const parsedLabels = safeParseJSON(data.labels, []);
      labels = Array.isArray(parsedLabels)
        ? parsedLabels.map((item) => (Array.isArray(item) ? item[1] : item))
        : [];
      const firstDataset =
        Array.isArray(parsedChartData) &&
          parsedChartData[0] &&
          Array.isArray(parsedChartData[0].data)
          ? parsedChartData[0].data
          : [];
      values = firstDataset.map((item) =>
        Array.isArray(item) ? item[1] : item,
      );
      // else if (portlet.id == 'SalesTopCustomerBar' || portlet.id == 'PriorityOrdersChart' || portlet.id == 'ShipmentBarChart'
      //   || portlet.id == 'QuotePerPersonChart' || portlet.id == 'OrderCycleTimeBar' || portlet.id == 'QuoteStatusBar' || portlet.id == 'OrderStatusBar'
      //   || portlet.id == 'SubscriptionStatus' || portlet.id == 'DunningCount' || portlet.id == 'InvoiceStatusCount') {

      //   const parsedChartData = JSON.parse(data.chartData);
      //   const parsedLabels = JSON.parse(data.labels);
      //   labels = parsedLabels.map(item => item[1]);
      //   values = parsedChartData[0].data.map(item => item[1]);

      if (data.shipmentTypeList) {
        setShipmentTypeList(data.shipmentTypeList);
      }
      if (data.statusList) {
        setStatusList(data.statusList);
      }
      if (data.quoteTypeList) {
        setQuoteTypeList(data.quoteTypeList);
      }
      if (data.quoteTypes) {
        setquotesMap(data.quoteTypes);
      }
      if (data.tenants) {
        setTenantsList(data.tenants);
      }
      if (data.orderTypes) {
        setOrderTypeMap(data.orderTypes);
      }
    }

    // Prepare chart data
    if (chartType === "line") {
      var refinedValues =
        values && Array.isArray(values)
          ? values.map((item, index) => {
            var obj = { ...item };
            obj.fill = false;
            if (index === 0) {
              obj.borderColor = "rgba(151, 71, 255, 1)";
              obj.backgroundColor = obj.borderColor;
            } else if (index === 1) {
              obj.borderColor = "rgba(255, 189, 189, 1)";
              obj.backgroundColor = obj.borderColor;
            } else {
              obj.borderColor = getRandomRGBA();
              obj.backgroundColor = obj.borderColor;
            }
            return obj;
          })
          : [];
      const preparedChartData = {
        labels: labels,
        datasets: [
          ...refinedValues,
          // {
          //   label: portlet.name,
          //   data: values,
          //   backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
          //   borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
          //   borderWidth: 1,
          // },
        ],
      };
      setChartData(preparedChartData);
    } else {
      if (chartType === "bar") {
        var refinedValues = values.map((item, index) => {
          var obj = { ...item };
          // obj.fill = false;
          if (index === 0) {
            obj.borderColor = "rgba(151, 71, 255, 1)";
            obj.backgroundColor = obj.borderColor;
          } else if (index === 1) {
            obj.borderColor = "rgba(255, 189, 189, 1)";
            obj.backgroundColor = obj.borderColor;
          } else {
            obj.borderColor = getRandomRGBA();
            obj.backgroundColor = obj.borderColor;
          }
          return obj;
        });
        const preparedChartData = {
          labels: labels,
          datasets: [
            ...refinedValues,
            // {
            //   label: portlet.name,
            //   data: values,
            //   backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
            //   borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
            //   borderWidth: 1,
            // },
          ],
        };
        setChartData(preparedChartData);
      } else {
        const preparedChartData = {
          labels: labels,
          datasets: [
            {
              label: portlet.name,
              data: values,
              // backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
              // borderColor: ['#36A2EB', '#FF6384', '#FFCE56'],
              backgroundColor: values.map((item) => "rgba(151, 71, 255, 1)"),
              borderColor: values.map((item) => "rgba(151, 71, 255, 1)"),
              borderWidth: 1,
            },
          ],
        };
        setChartData(preparedChartData);
      }
    }
    setLoading(false);
  };

  const safeParseJSON = (maybeJSON, fallback) => {
    try {
      return typeof maybeJSON === "string"
        ? JSON.parse(maybeJSON)
        : (maybeJSON ?? fallback);
    } catch {
      return fallback;
    }
  };

  if (!chartData) {
    // return <div>Error loading data</div>;
    return (
      // <div className={classes.screenlet}>
      <div
        className={"roundCorners borderOutline"}
        style={{ background: "white", marginTop: "1rem" }}
      >
        <div className={classes.screenlet_body}>
          <p
            className={
              // classes.graph_name
              classes.graph_description
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: "inherit",
            }}
          >
            {portlet.name}
            {/* <Tooltip title={portlet.description} arrow>
              <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'pointer' }}>
                <InfoOutlinedIcon fontSize="small" style={{ color: 'inherit', fontSize: 16, verticalAlign: 'middle' }} />
              </span>
            </Tooltip> */}
            <InfoWithMobilePopover description={portlet.description} />
          </p>
          {/* <p className={classes.graph_description}>{portlet.description}</p> */}
          <p>{t("NO_RESULTS_FOUND")}</p>
        </div>
      </div>
    );
  }

  return (
    // <div className={classes.screenlet}>
    <div
      className={"roundCorners borderOutline"}
      style={{ background: "white" }}
    >
      <div className={classes.screenlet_body}>
        <div
          className={`${classes.dflex_between_center} ${classes.gap_column}`}
        >
          <div
            style={{
              flex: 1,
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <p
              className={
                // classes.graph_name
                classes.graph_description
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                color: "inherit",
              }}
            >
              {portlet.name}
              {/* <Tooltip title={portlet.description} arrow>
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'pointer' }}>
                  <InfoOutlinedIcon fontSize="small" style={{ color: 'inherit', fontSize: 16, verticalAlign: 'middle' }} />
                </span>
              </Tooltip> */}
              <InfoWithMobilePopover description={portlet.description} />
            </p>
            {portletData &&
              portletData.graphTypes &&
              Object.keys(portletData.graphTypes).length > 0 && (
                // <div className={classes.dflex_justify_align_items}>
                <FormControl
                  variant="outlined"
                  size={"small"}
                  style={{ width: "150px" }}
                >
                  <InputLabel id="graphType">{t("GRAPH_TYPE")}</InputLabel>
                  <Select
                    native
                    value={selectedGraphType}
                    onChange={handleGraphTypeChange}
                    name="graphType"
                    label={t("GRAPH_TYPE")}
                    labelId="graphType"
                    inputProps={{
                      id: "graphType",
                    }}
                  >
                    {Object.entries(portletData.graphTypes).map(
                      ([key, value]) => (
                        <option key={value} value={value}>
                          {key}
                        </option>
                      ),
                    )}
                  </Select>
                </FormControl>
                // </div>
              )}
          </div>
          {shipmentTypeList &&
            (portlet.id == "ShipmentPieChart" ||
              portlet.id == "ShipmentBarChart") ? (
            <div className={classes.dflex_justify_align_items}>
              {/* <label className={classes.dashboard_label}>Type</label> */}
              {/* <select name="shipmentType" value={shipmentType} onChange={handleShipmentTypeChange}>
                <option value="">Select Shipment Types</option>
                  {shipmentTypeList.map((type) => (
                    <option key={type.shipmentTypeId} value={type.shipmentTypeId}>
                      {type.description}
                    </option>
                  ))}
              </select> */}
              <FormControl
                variant="outlined"
                size={"small"}
                style={{ width: "150px" }}
              >
                <InputLabel id="shipmentType">{t("TYPE")}</InputLabel>
                <Select
                  native
                  value={shipmentType}
                  onChange={handleShipmentTypeChange}
                  name="shipmentType"
                  label="Type"
                  labelId="shipmentType"
                  inputProps={{
                    id: "shipmentType",
                  }}
                >
                  <option value=" ">{t("SELECT_SHIPMENT_TYPE")}</option>
                  {shipmentTypeList.map((type) => (
                    <option
                      key={type.shipmentTypeId}
                      value={type.shipmentTypeId}
                    >
                      {type.description}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            ""
          )}

          {portlet.id == "QuotePerPersonChart" ? (
            <>
              {statusList ? (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Status</label> */}
                  {/* <select name="statusType" value={statusType} onChange={handleStatusChange}>
                    <option value="">Select Status</option>
                    {statusList.map((type) => (
                      <option key={type.statusId} value={type.statusId}>
                        {type.description}
                      </option>
                    ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="statusType">{t("STATUS")}</InputLabel>
                    <Select
                      native
                      value={statusType}
                      onChange={handleStatusChange}
                      name="statusType"
                      label={t("STATUS")}
                      labelId="statusType"
                      inputProps={{
                        id: "statusType",
                      }}
                    >
                      <option value=" ">Select Status</option>
                      {statusList.map((type) => (
                        <option key={type.statusId} value={type.statusId}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              ) : (
                ""
              )}

              {quoteTypeList ? (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Quote Type</label> */}
                  {/* <select name="quoteType" value={quoteType} onChange={handleQuoteTypeChange}>
                    <option value="">Select Type</option>
                    {quoteTypeList.map((type) => (
                      <option key={type.quoteTypeId} value={type.quoteTypeId}>
                        {type.description}
                      </option>
                    ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="quoteType">{t("QUOTE_TYPE")}</InputLabel>
                    <Select
                      native
                      value={quoteType}
                      onChange={handleQuoteTypeChange}
                      name="quoteType"
                      label={t("QUOTE_TYPE")}
                      labelId="quoteType"
                      inputProps={{
                        id: "quoteType",
                      }}
                    >
                      <option value=" ">Select Type</option>
                      {quoteTypeList.map((type) => (
                        <option key={type.quoteTypeId} value={type.quoteTypeId}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              ) : (
                ""
              )}
            </>
          ) : (
            ""
          )}

          {portlet.id == "QuoteStatusBar" ? (
            <>
              {Object.keys(quoteTypeMap).length > 0 ? (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Quote Status</label> */}
                  {/* <select name="typeId" value={typeId} onChange={handleQuoteStatusChange}>
                    <option value="All">All</option>
                    {Object.entries(quoteTypeMap).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="quoteStatusType">
                      {t("QUOTE_STATUS")}
                    </InputLabel>
                    <Select
                      native
                      value={typeId}
                      onChange={handleQuoteStatusChange}
                      name="typeId"
                      label={t("QUOTE_STATUS")}
                      labelId="quoteStatusType"
                      inputProps={{
                        id: "typeId",
                      }}
                    >
                      <option value="All">All</option>
                      {Object.entries(quoteTypeMap).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              ) : (
                ""
              )}

              {tenantsList && tenantsList.length > 0 && (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Tenant</label> */}
                  {/* <select name="partnerId" value={partnerId} onChange={handlePartnerIdChange}>
                    {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="tenantType">{t("TENANT")}</InputLabel>
                    <Select
                      native
                      value={partnerId}
                      onChange={handlePartnerIdChange}
                      name="partnerId"
                      label={t("TENANT")}
                      labelId="tenantType"
                      inputProps={{
                        id: "partnerId",
                      }}
                    >
                      {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          ) : (
            ""
          )}

          {portlet.id == "OrderStatusBar" ? (
            <>
              {Object.keys(orderTypeMap).length > 0 ? (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Order Status</label> */}
                  {/* <select name="typeId" value={typeId} onChange={handleOrderStatusChange}>
                    <option value="All">All</option>
                    {Object.entries(orderTypeMap).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="orderStatusType">
                      {t("ORDER_STATUS")}
                    </InputLabel>
                    <Select
                      native
                      value={typeId}
                      onChange={handleOrderStatusChange}
                      name="typeId"
                      label={t("ORDER_STATUS")}
                      labelId="orderStatusType"
                      inputProps={{
                        id: "typeId",
                      }}
                    >
                      <option value="All">All</option>
                      {Object.entries(orderTypeMap).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              ) : (
                ""
              )}

              {tenantsList && tenantsList.length > 0 && (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Tenant</label> */}
                  {/* <select name="partnerId" value={partnerId} onChange={handlePartnerIdChange}>
                    {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="tenantType">{t("TENANT")}</InputLabel>
                    <Select
                      native
                      value={partnerId}
                      onChange={handlePartnerIdChange}
                      name="partnerId"
                      label={t("TENANT")}
                      labelId="tenantType"
                      inputProps={{
                        id: "partnerId",
                      }}
                    >
                      {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          ) : (
            ""
          )}

          {portlet.id == "SubscriptionStatus" ? (
            <>
              {tenantsList && tenantsList.length > 0 && (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Tenant</label> */}
                  {/* <select name="partnerId" value={partnerId} onChange={handlePartnerIdChange}>
                    {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="tenantType">{t("TENANT")}</InputLabel>
                    <Select
                      native
                      value={partnerId}
                      onChange={handlePartnerIdChange}
                      name="partnerId"
                      label={t("TENANT")}
                      labelId="tenantType"
                      inputProps={{
                        id: "partnerId",
                      }}
                    >
                      {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          ) : (
            ""
          )}

          {portlet.id == "DunningCount" ? (
            <>
              {tenantsList && tenantsList.length > 0 && (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Tenant</label> */}
                  {/* <select name="partnerId" value={partnerId} onChange={handlePartnerIdChange}>
                    {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="tenantType">{t("TENANT")}</InputLabel>
                    <Select
                      native
                      value={partnerId}
                      onChange={handlePartnerIdChange}
                      name="partnerId"
                      label={t("TENANT")}
                      labelId="tenantType"
                      inputProps={{
                        id: "partnerId",
                      }}
                    >
                      {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          ) : (
            ""
          )}

          {portlet.id == "InvoiceStatusCount" ? (
            <>
              {tenantsList && tenantsList.length > 0 && (
                <div className={classes.dflex_justify_align_items}>
                  {/* <label className={classes.dashboard_label}>Tenant</label> */}
                  {/* <select name="partnerId" value={partnerId} onChange={handlePartnerIdChange}>
                    {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </select> */}
                  <FormControl
                    variant="outlined"
                    size={"small"}
                    style={{ width: "150px" }}
                  >
                    <InputLabel id="tenantType">{t("TENANT")}</InputLabel>
                    <Select
                      native
                      value={partnerId}
                      onChange={handlePartnerIdChange}
                      name="partnerId"
                      label={t("TENANT")}
                      labelId="tenantType"
                      inputProps={{
                        id: "partnerId",
                      }}
                    >
                      {tenantsList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          ) : (
            ""
          )}
        </div>
        {chartData &&
          Array.isArray(chartData.labels) &&
          chartData.labels.length > 0 ? (
          selectedGraphType === "line" ? (
            <Line data={chartData} options={lineChartOptions} />
          ) : selectedGraphType === "pie" ? (
            <Pie data={chartData} options={pieChartOptions} />
          ) : (
            <Bar data={chartData} options={barChartOptions} />
          )
        ) : (
          <p>{t("NO_RESULTS_FOUND")}</p>
        )}
      </div>
      {/* {loading && <Loader loading={loading} />} */}
    </div>
  );
};

export default GraphPortlet;
